// A swap's receipt from the confirmed transaction itself (R46): what the
// vault's output account gained, what the program moved to the treasury,
// and what the treasury's account actually gained, all from that one
// transaction's `preTokenBalances`/`postTokenBalances` (and, for a native
// input, output or fee account, `preBalances`/`postBalances`, R63) and its
// inner instructions.
// Nothing here reads a live account, so a deposit into the output account or
// another user's fee landing in the shared treasury account between two reads
// can never be mistaken for this swap's figures.
//
// The program's fee leg (program/src/instructions/execute.rs) is one inner
// SPL Token `TransferChecked` (tag 12, `amount` u64 le, `decimals`) from the
// paying account to the treasury's fee account, signed by the vault; its
// amount is the protocol's debit. The paying account is fixed by the two
// signed mints (`feeSide`, program/src/fee.rs): the vault's output account on
// the output side (the fee is 30 bps of the gross output, the user keeps the
// rest), the vault's input account on the input side (30 bps of the input
// the swap spent, taken after the swap; the user keeps the whole output).
// With a Token-2022 transfer-fee mint the treasury's spendable credit is
// smaller than that debit (the difference is withheld by the mint, accepted
// at G5 part 1); both are reported apart. Input-side mints (USDC, USDT, wSOL)
// are SPL Token mints and withhold nothing.

import { address, getBase58Encoder } from '@solana/kit';
import { type Expected, type FeeSide, WSOL_MINT_ADDRESS, feeSide as feeSideOf, protocolFee } from './jupiter.js';
import { addressBytes, addressFromBytes } from './vault.js';

/** One entry of `getTransaction`'s `preTokenBalances` / `postTokenBalances`. */
export interface TokenBalanceEntry {
  accountIndex: number;
  mint: string;
  owner?: string;
  uiTokenAmount: { amount: string; decimals: number };
}

/** The parts of a `getTransaction` (encoding `json`) response a receipt needs. */
export interface TransactionJson {
  slot?: number;
  meta?: {
    err?: unknown;
    /** Lamports per account key before and after; read for a native (wrapped SOL) input or output account, whose token `amount` can lag its lamports until a SyncNative (R63). A decimal string is exact; a JSON number is exact only up to `Number.MAX_SAFE_INTEGER`, and a larger one is refused (R64). */
    preBalances?: (number | string)[];
    postBalances?: (number | string)[];
    preTokenBalances?: TokenBalanceEntry[];
    postTokenBalances?: TokenBalanceEntry[];
    innerInstructions?: { index: number; instructions: { programIdIndex: number; accounts: number[]; data: string }[] }[];
  } | null;
  /** `signatures` and `message.instructions` are read only by the R69 proof (applied.ts, docs/17 2.4); the receipt reads neither. */
  transaction?: { signatures?: string[]; message?: { accountKeys?: string[]; instructions?: { programIdIndex: number; accounts: number[]; data: string }[] } };
}

/** The accounts a receipt is read from, all base58, and the side the program charges. */
export interface SwapReceiptAccounts {
  /** The vault's input token account: the paying account on the input side; on either side it yields `spent`. */
  inAta: string;
  /** The vault's output token account. */
  outAta: string;
  /** The treasury's token account for the fee mint (`swapFeeAccount`). */
  feeAccount: string;
  /** `feeSide(inMint, outMint)` of the signed mints; checked against the mints the transaction's own balances record. */
  feeSide: FeeSide;
  /** The signed `min_out`: the program settles only if `gross` reaches it, so a readback below it is refused as incomplete. */
  minOut?: bigint;
  /** The signed `max_in`: the program settles only if `spent` (plus the fee on the input side) stays within it, so a readback above it is refused as incomplete. */
  maxIn?: bigint;
}

export interface SwapReceipt {
  /** The side that paid the protocol fee. */
  feeSide: FeeSide;
  /** The mint the fee was paid in, base58: the output mint on the output side, the input mint on the input side. */
  feeMint: string;
  /** Credited to the vault's output account by this transaction: what the user keeps (on the input side, all the route delivered). */
  received: bigint;
  /** What the program moved from the paying account (the output account, or the input account on the input side) to the treasury's fee account. */
  protocolDebit: bigint;
  /** What the treasury's fee account actually gained; below `protocolDebit` only when the mint withholds a transfer fee. */
  treasuryCredit: bigint;
  withheld: bigint;
  /** What the route delivered before any fee: `received + protocolDebit` on the output side, `received` on the input side. */
  gross: bigint;
  /** The input the swap itself took, the fee excluded (on the input side, the input account's decrease minus `protocolDebit`). Present when the input account was given. */
  spent?: bigint;
}

export type ReceiptResult = { ok: true; receipt: SwapReceipt } | { ok: false; reason: string };

const TOKEN_TRANSFER_CHECKED = 12;
const base58 = getBase58Encoder();

function entry(list: TokenBalanceEntry[] | undefined, index: number): TokenBalanceEntry | undefined {
  return list?.find((b) => b.accountIndex === index);
}

function tokenDelta(tx: TransactionJson, index: number): bigint | null {
  const pre = entry(tx.meta?.preTokenBalances, index);
  const post = entry(tx.meta?.postTokenBalances, index);
  // No post entry means the account was not a token account when the transaction ended: nothing to read.
  if (!post) return null;
  return BigInt(post.uiTokenAmount.amount) - BigInt(pre?.uiTokenAmount.amount ?? '0');
}

/** The mint a token account holds, as the transaction's own balances record it. */
function mintOf(tx: TransactionJson, index: number): string | undefined {
  return (entry(tx.meta?.postTokenBalances, index) ?? entry(tx.meta?.preTokenBalances, index))?.mint;
}

function mintBytes(mint: string): Uint8Array | null {
  try {
    return addressBytes(address(mint));
  } catch {
    return null;
  }
}

const NO_LAMPORTS = 'the transaction carries no lamport balances for the wrapped SOL account, which is measured in lamports';
const INEXACT_LAMPORTS = 'a lamport balance of the wrapped SOL account is not an exact integer as read (a JSON number above 2^53 - 1 has already been rounded)';

/**
 * A lamport balance exactly as the response gave it, or null. A u64 balance
 * that arrives as a JSON number above `Number.MAX_SAFE_INTEGER` has already
 * been rounded by `JSON.parse`, and `BigInt` cannot restore the lost bits, so
 * it is refused rather than read (R64); a decimal string stays exact.
 */
function exactLamports(value: number | string): bigint | null {
  if (typeof value === 'string') return /^[0-9]+$/.test(value) ? BigInt(value) : null;
  return Number.isSafeInteger(value) && value >= 0 ? BigInt(value) : null;
}

/** The account's lamports before and after, or why they cannot be read exactly. */
function lamportsOf(tx: TransactionJson, index: number): { pre: bigint; post: bigint } | string {
  const pre = tx.meta?.preBalances?.[index];
  const post = tx.meta?.postBalances?.[index];
  if (pre === undefined || post === undefined) return NO_LAMPORTS;
  const [p, q] = [exactLamports(pre), exactLamports(post)];
  return p === null || q === null ? INEXACT_LAMPORTS : { pre: p, post: q };
}

/** Token-2022's native mint. The program treats any native token account as wrapped SOL (lamports above the reserve), so the receipt reads both native mints the same way. */
const TOKEN_2022_NATIVE_MINT_ADDRESS = '9pan9bMn5HatX4EJdBwg9VgCa7Uz5HL8N1m5D3NdXejP';

function isNative(mint: string | undefined): boolean {
  return mint === WSOL_MINT_ADDRESS || mint === TOKEN_2022_NATIVE_MINT_ADDRESS;
}

/**
 * How much the input account lost over the transaction, or why it cannot be
 * read. A native (wrapped SOL) account is measured in lamports, as the
 * program measures it: its token `amount` may not reflect lamports sent to
 * it without a SyncNative, while the fee leg syncs it before paying.
 */
function inputDecrease(tx: TransactionJson, index: number, mint: string | undefined): bigint | string {
  if (isNative(mint)) {
    const l = lamportsOf(tx, index);
    return typeof l === 'string' ? l : l.pre - l.post;
  }
  const d = tokenDelta(tx, index);
  return d === null ? 'the transaction carries no balance for the input account' : -d;
}

/**
 * What an account that only gains in a swap (the output account, after the
 * fee on the output side; the treasury's fee account) gained over the
 * transaction, or why it cannot be read. A native (wrapped SOL) account is
 * measured in lamports, as the program measures it (R63): a route can
 * deliver raw lamports that `amount` shows only after a SyncNative, and
 * lamports an earlier deposit left unsynced enter `amount` when the account
 * is synced but were never part of this swap. For an account that existed
 * before the transaction the lamport change is exact: its rent reserve is
 * the same before and after, and only the route and the fee leg move its
 * lamports (a token transfer moves a native account's lamports with its
 * `amount`). For one the transaction's own ATA instruction created, the
 * lamport change also holds the rent paid in at creation, which the
 * metadata does not name, while the closing `amount` (synced: the program
 * syncs a native output after every settled swap, and the fee leg's
 * transfer keeps a fee account synced) also holds whatever the address
 * held above the rent reserve before it. Each can only overstate, and one
 * of the two is always exact, so the smaller is what arrived.
 */
function increase(tx: TransactionJson, index: number, mint: string | undefined, tokens: bigint): bigint | string {
  if (!isNative(mint)) return tokens;
  const l = lamportsOf(tx, index);
  if (typeof l === 'string') return l;
  const lamports = l.post - l.pre;
  if (entry(tx.meta?.preTokenBalances, index)) return lamports;
  return lamports < tokens ? lamports : tokens;
}

/** The receipt accounts of a swap from the `Expected` it was assembled with (base58, and the fee side of its two mints). */
export function receiptAccounts(expect: Pick<Expected, 'inAta' | 'outAta' | 'feeAccount' | 'inMint' | 'outMint'>): SwapReceiptAccounts {
  return {
    inAta: addressFromBytes(expect.inAta),
    outAta: addressFromBytes(expect.outAta),
    feeAccount: addressFromBytes(expect.feeAccount),
    feeSide: feeSideOf(expect.inMint, expect.outMint),
  };
}

/**
 * The receipt of a landed swap, or the reason none can be read. Pass the
 * swap's accounts and fee side (`receiptAccounts(expect)`); the older form
 * `swapReceipt(tx, outAta, feeAccount)` reads an output-side receipt without
 * `spent`. With a fee owed (`protocolFee` of the gross output, or of the
 * input spent, above zero) and no fee transfer from the paying account in the
 * transaction, the receipt is refused rather than reported with a fee of zero.
 */
export function swapReceipt(tx: TransactionJson, accounts: SwapReceiptAccounts): ReceiptResult;
/** Output side only, as before the fee side existed: `outAta` and `feeAccount` are base58. */
export function swapReceipt(tx: TransactionJson, outAta: string, feeAccount: string): ReceiptResult;
export function swapReceipt(tx: TransactionJson, accountsOrOutAta: SwapReceiptAccounts | string, legacyFeeAccount?: string): ReceiptResult {
  const a: { inAta?: string; outAta: string; feeAccount: string; feeSide: FeeSide; minOut?: bigint; maxIn?: bigint } =
    typeof accountsOrOutAta === 'string' ? { outAta: accountsOrOutAta, feeAccount: legacyFeeAccount ?? '', feeSide: 'output' } : accountsOrOutAta;
  const side = a.feeSide;
  const keys = tx.transaction?.message?.accountKeys;
  if (!tx.meta || !keys) return { ok: false, reason: 'the transaction could not be read back' };
  if (tx.meta.err) return { ok: false, reason: 'the transaction failed on chain' };
  if (!tx.meta.postTokenBalances) return { ok: false, reason: 'the transaction carries no token balance changes' };
  const outIndex = keys.indexOf(a.outAta);
  const feeIndex = keys.indexOf(a.feeAccount);
  if (outIndex < 0 || feeIndex < 0) return { ok: false, reason: 'the transaction does not name both the output account and the fee account' };
  const inIndex = a.inAta === undefined ? -1 : keys.indexOf(a.inAta);
  if (a.inAta !== undefined && inIndex < 0) return { ok: false, reason: 'the transaction does not name the input account' };
  const outTokens = tokenDelta(tx, outIndex);
  const feeTokens = tokenDelta(tx, feeIndex);
  if (outTokens === null || feeTokens === null) return { ok: false, reason: 'the transaction carries no balance for the output account or the fee account' };
  const inMint = inIndex < 0 ? undefined : mintOf(tx, inIndex);
  const outMint = mintOf(tx, outIndex);
  const received = increase(tx, outIndex, outMint, outTokens);
  if (typeof received === 'string') return { ok: false, reason: received };
  if (received < 0n) return { ok: false, reason: 'the output account shows a loss, not a delivery' };
  if (inIndex >= 0) {
    const inBytes = inMint === undefined ? null : mintBytes(inMint);
    const outBytes = mintBytes(outMint!);
    if (!inBytes || !outBytes) return { ok: false, reason: 'the transaction carries no readable mint for the input or the output account' };
    const actual = feeSideOf(inBytes, outBytes);
    if (actual !== side) return { ok: false, reason: `the transaction's mints put the fee on the ${actual} side, not the ${side} side` };
  }
  const payIndex = side === 'input' ? inIndex : outIndex;
  const feeMint = mintOf(tx, feeIndex)!;
  if (feeMint !== (side === 'input' ? inMint : outMint)) return { ok: false, reason: `the fee account holds a different mint than the vault's ${side} account` };
  const treasuryCredit = increase(tx, feeIndex, feeMint, feeTokens);
  if (typeof treasuryCredit === 'string') return { ok: false, reason: treasuryCredit };
  let protocolDebit = 0n;
  let transferSeen = false;
  for (const group of tx.meta.innerInstructions ?? []) {
    for (const ix of group.instructions) {
      if (ix.accounts.length !== 4 || ix.accounts[0] !== payIndex || ix.accounts[2] !== feeIndex) continue;
      const data = base58.encode(ix.data);
      if (data.length !== 10 || data[0] !== TOKEN_TRANSFER_CHECKED) continue;
      protocolDebit += new DataView(data.buffer, data.byteOffset, data.byteLength).getBigUint64(1, true);
      transferSeen = true;
    }
  }
  if (!transferSeen && treasuryCredit !== 0n) return { ok: false, reason: `the fee account changed without a fee transfer from the vault's ${side} account` };
  if (treasuryCredit > protocolDebit) return { ok: false, reason: 'the fee account gained more than the vault paid it' };
  let spent: bigint | undefined;
  if (inIndex >= 0) {
    const decrease = inputDecrease(tx, inIndex, inMint);
    if (typeof decrease === 'string') return { ok: false, reason: decrease };
    spent = side === 'input' ? decrease - protocolDebit : decrease;
    if (spent < 0n) return { ok: false, reason: 'the input account did not lose the fee it paid' };
  }
  // 30 bps floors to zero below 334 units; above it a fee was owed (on the gross output, or on the input spent), so a missing transfer means the readback is incomplete, not that the fee was zero.
  if (!transferSeen && protocolFee(side === 'input' ? spent! : received) > 0n) return { ok: false, reason: 'no fee transfer was found in the transaction although one was owed' };
  const gross = side === 'input' ? received : received + protocolDebit;
  // The program settled within the signed bounds; figures outside them mean the readback missed something, never that the bounds failed.
  if (a.minOut !== undefined && gross < a.minOut) return { ok: false, reason: 'the transaction reads as less arriving than the signed minimum the program enforced' };
  if (a.maxIn !== undefined && spent !== undefined && spent + (side === 'input' ? protocolDebit : 0n) > a.maxIn) return { ok: false, reason: 'the transaction reads as more leaving the input account than the signed maximum the program enforced' };
  const receipt: SwapReceipt = { feeSide: side, feeMint, received, protocolDebit, treasuryCredit, withheld: protocolDebit - treasuryCredit, gross };
  if (spent !== undefined) receipt.spent = spent;
  return { ok: true, receipt };
}
